import type { Dispatch, SetStateAction } from 'react';

import { Dialog, screenReaderOnlyCSS, SubmitButton, truncatedEllipsizedTextCSS } from '@capsule/consumer-design-system';

import { captureException } from '@sentry/react';
import type { FormikErrors } from 'formik';
import { Form, Field, Formik } from 'formik';
import styled from 'styled-components';

import { LoginMfaOption } from 'src/app/context/AuthContext';
import useAuth from 'src/app/hooks/useAuth';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';

// TODO: Abstract Radio input into reusable components
const RadioGroup = styled.div.attrs({ role: 'group' })`
  display: flex;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.space.s5};
  color: ${({ theme }) => theme.colors.capsuleBlue60};
  width: 100%;
`;

const radioCircleDiameter = '2rem';

export const RadioCircle = styled.span`
  position: relative;
  display: block;
  height: ${radioCircleDiameter};
  width: ${radioCircleDiameter};
  border: 1px solid ${({ theme }) => theme.colors.capsuleBlue50};
  border-radius: ${({ theme }) => theme.radii.circle};

  &:after {
    position: absolute;
    content: '';
    display: none;
    top: 0.2rem;
    left: 0.2rem;
    width: 1.4rem;
    height: 1.4rem;
    border-radius: ${({ theme }) => theme.radii.circle};
    background: ${({ theme }) => theme.colors.capsuleBlue50};
  }
`;

const RadioItem = styled.label`
  position: relative;
  display: flex;
  flex-direction: row;
  cursor: pointer;
  width: 100%;
  padding: ${({ theme }) => theme.space.s4} ${({ theme }) => theme.space.s1};
  border-top: 1px solid ${({ theme }) => theme.colors.capsuleGreen10};

  /* box-shadow is used here to make a thicker ring without shifting content. */
  /* One day, we can just use outline, but outline doesn't adhere to border-radius on Safari. */
  &:focus-within > ${RadioCircle}, &:hover > ${RadioCircle} {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.capsuleBlue50};
  }

  & > input:checked ~ ${RadioCircle} {
    box-shadow: none;
  }

  /* Make "checked" styles visible */
  & > input:checked ~ ${RadioCircle}:after {
    display: block;
  }

  /* Little border lines */
  &:last-of-type {
    border-bottom: 1px solid ${({ theme }) => theme.colors.capsuleGreen10};
  }
`;

const InvisibleRadioInput = styled(Field).attrs({ type: 'radio' })`
  ${screenReaderOnlyCSS}
`;

const FlexSpaceBetween = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: calc(100% - ${radioCircleDiameter});
`;

const RadioItemTitle = styled.span`
  padding: 0 ${({ theme }) => theme.space.s3};
`;

const RadioItemHelper = styled.span`
  color: ${({ theme }) => theme.colors.capsuleGray50};
  ${truncatedEllipsizedTextCSS}
`;

export interface MFAResendDialogProps {
  isResendDialogOpen: boolean;
  resetMFAForm: () => void;
  setMFAFormErrors: (errors: FormikErrors<{ _generic?: string }>) => void;
  setSelectedMfaDeliveryMedium: Dispatch<SetStateAction<LoginMfaOption>>;
  setResendDialogOpen: (isOpen: boolean) => void;
  isEmailOptionDisabled: boolean;
}

export const MFAResendDialog = ({
  isResendDialogOpen,
  resetMFAForm,
  setMFAFormErrors,
  setSelectedMfaDeliveryMedium,
  setResendDialogOpen,
  isEmailOptionDisabled,
}: MFAResendDialogProps) => {
  const dispatchViewMfaModalEvent = useSegmentEvent('viewMfaModal', { shouldTrackOnMount: false });
  const dispatchCloseMfaModalEvent = useSegmentEvent('closeMfaModal', { shouldTrackOnMount: false });
  const segmentTrackCodeResend = useSegmentEvent('resendVerificationCode', { shouldTrackOnMount: false });

  const { login, loginByCode, loginContext, loginResult } = useAuth();

  return (
    <Dialog
      title="How should we send your code?"
      isOpen={isResendDialogOpen}
      onOpenChange={setResendDialogOpen}
      onOpen={dispatchViewMfaModalEvent}
      onClose={dispatchCloseMfaModalEvent}
    >
      <Formik<{ resendMethod: 'text' | 'email' }>
        initialValues={{ resendMethod: 'text' }}
        enableReinitialize={true}
        onSubmit={async ({ resendMethod }, { setSubmitting }) => {
          try {
            setSubmitting(true);

            segmentTrackCodeResend({ channel: resendMethod });

            const mfaOption = resendMethod === 'email' ? LoginMfaOption.EMAIL : LoginMfaOption.SMS;
            setSelectedMfaDeliveryMedium(mfaOption);

            if (!!loginContext.checkoutCode) {
              await loginByCode(loginContext.userUuid, loginContext.checkoutCode);
            } else {
              await login(loginContext?.email, loginContext?.password, mfaOption);
            }

            /**
             * We're purposefully elongating this loading state so that users feel confident that a
             * resend was successfully triggered. Our back end was too fast! ⚡️
             */
            await new Promise(resolve => setTimeout(resolve, 1000)).then(() => {
              setResendDialogOpen(false);
              resetMFAForm();
            });
          } catch (error: any) {
            captureException(error);
            setMFAFormErrors({
              _generic: 'Something isn’t quite right on our end... Please try again or contact the pharmacy for help.',
            });
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <RadioGroup>
              <RadioItem>
                <InvisibleRadioInput name="resendMethod" value="text" />

                <RadioCircle />

                <FlexSpaceBetween>
                  <RadioItemTitle>Text</RadioItemTitle>
                  {/* @ts-expect-error:next-line */}
                  <RadioItemHelper>{loginResult?.nextStep.codeDeliveryDetails?.destination}</RadioItemHelper>
                </FlexSpaceBetween>
              </RadioItem>

              {!isEmailOptionDisabled && (
                <RadioItem>
                  <InvisibleRadioInput name="resendMethod" value="email" />

                  <RadioCircle />

                  <FlexSpaceBetween>
                    <RadioItemTitle>Email</RadioItemTitle>
                    <RadioItemHelper>{loginContext?.email}</RadioItemHelper>
                  </FlexSpaceBetween>
                </RadioItem>
              )}
            </RadioGroup>

            <SubmitButton variant="contained-secondary-lg" isSubmitting={isSubmitting} isFullWidth>
              Send
            </SubmitButton>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
